import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Plus, Search, DollarSign, FileText, Calendar, Phone, Mail,
  Clock, CheckCircle, AlertCircle, Download, Send, Printer,
  CreditCard, Banknote, X, User, Link, Copy, Eye, MapPin, Receipt
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import PayInvoiceButton from './PayInvoiceButton';
import jsPDF from 'jspdf';
import { formatDisplayDate } from './dateUtils';

export default function InvoicingModule({ companyId, onNavigate, embedded = false }) {
  const { colors } = useTheme();
  const { showAlert, showSuccess, showError, showConfirm } = useModal();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPayment, setShowAddPayment] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [jobs, setJobs] = useState([]); // Completed jobs without invoices
  const [customers, setCustomers] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [companyStripeId, setCompanyStripeId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);

  // Direct invoice creation state
  const [showDirectCreate, setShowDirectCreate] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_id: '',
    description: '',
    amount: '',
    tax_rate: 8,
    due_days: 14,
    notes: ''
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [salesTaxMode, setSalesTaxMode] = useState('optional'); // 'required', 'optional', 'disabled'

  // Processing fee pass-through state
  const [processingFeeEnabled, setProcessingFeeEnabled] = useState(false);
  const [defaultFeePercent, setDefaultFeePercent] = useState(3.0);
  const [addProcessingFee, setAddProcessingFee] = useState(false);

  const statuses = [
    { value: 'unpaid', label: 'Unpaid', color: colors.error, icon: AlertCircle },
    { value: 'partial', label: 'Partial', color: colors.warning, icon: Clock },
    { value: 'paid', label: 'Paid', color: colors.success, icon: CheckCircle },
    { value: 'overdue', label: 'Overdue', color: colors.error, icon: AlertCircle }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: Banknote },
    { value: 'check', label: 'Check', icon: FileText },
    { value: 'credit_card', label: 'Credit Card', icon: CreditCard },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: DollarSign }
  ];

  useEffect(() => {
    if (companyId) {
      loadInvoices();
      loadCompanyInfo();
      loadCustomers();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadCompanyInfo = async () => {
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompanyStripeId(data?.stripe_connect_id || null);
      setCompanyName(data?.name || '');
      setCompanyInfo(data);
      setSalesTaxMode(data?.sales_tax_mode || 'optional');
      // Update newInvoice tax_rate based on sales_tax_mode
      if (data?.sales_tax_mode === 'disabled') {
        setNewInvoice(prev => ({ ...prev, tax_rate: 0 }));
      }

      // Load processing fee settings
      setProcessingFeeEnabled(data?.processing_fee_enabled || false);
      setDefaultFeePercent(data?.default_processing_fee_percent || 3.0);
    } catch (error) {
    }
  };

  // View invoice with items
  const viewInvoice = async (invoice) => {
    setViewingInvoice(invoice);
    try {
      const { data: items } = await supabaseData
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('created_at');
      setInvoiceItems(items || []);
    } catch (error) {
      setInvoiceItems([]);
    }
  };

  // Generate PDF for invoice
  const generateInvoicePDF = (invoice, items) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Company header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.name || 'Company', 20, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    if (companyInfo?.phone) {
      doc.text(companyInfo.phone, 20, y);
      y += 5;
    }
    if (companyInfo?.email) {
      doc.text(companyInfo.email, 20, y);
      y += 5;
    }
    if (companyInfo?.address) {
      const addr = `${companyInfo.address}${companyInfo.city ? ', ' + companyInfo.city : ''}${companyInfo.state ? ', ' + companyInfo.state : ''} ${companyInfo.zip || ''}`;
      doc.text(addr, 20, y);
      y += 5;
    }

    // Invoice title
    y += 10;
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - 20, 25, { align: 'right' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoice.invoice_number}`, pageWidth - 20, 35, { align: 'right' });
    doc.text(`Date: ${formatDisplayDate(invoice.created_at?.split('T')[0])}`, pageWidth - 20, 42, { align: 'right' });
    doc.text(`Due: ${formatDisplayDate(invoice.due_date)}`, pageWidth - 20, 49, { align: 'right' });

    // Bill To
    y = 70;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, y);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(invoice.customerName, 20, y);
    y += 5;
    if (invoice.customers?.phone) {
      doc.text(invoice.customers.phone, 20, y);
      y += 5;
    }
    if (invoice.customers?.email) {
      doc.text(invoice.customers.email, 20, y);
      y += 5;
    }
    if (invoice.customers?.address) {
      const custAddr = `${invoice.customers.address}${invoice.customers.city ? ', ' + invoice.customers.city : ''}${invoice.customers.state ? ', ' + invoice.customers.state : ''} ${invoice.customers.zip || ''}`;
      doc.text(custAddr, 20, y);
      y += 5;
    }

    // Items table header
    y += 15;
    doc.setFillColor(51, 51, 51);
    doc.rect(20, y - 5, pageWidth - 40, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 25, y + 2);
    doc.text('Qty', 120, y + 2);
    doc.text('Price', 145, y + 2);
    doc.text('Total', pageWidth - 25, y + 2, { align: 'right' });

    // Items
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 12;

    if (items && items.length > 0) {
      items.forEach(item => {
        doc.text(item.description || item.name || 'Item', 25, y);
        doc.text(String(item.quantity || 1), 125, y);
        doc.text(`$${(item.unit_price || item.price || 0).toFixed(2)}`, 145, y);
        doc.text(`$${((item.quantity || 1) * (item.unit_price || item.price || 0)).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
        y += 8;
      });
    } else {
      doc.text('Services rendered', 25, y);
      doc.text('1', 125, y);
      doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, 145, y);
      doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 8;
    }

    // Totals
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(120, y, pageWidth - 20, y);
    y += 8;

    doc.text('Subtotal:', 120, y);
    doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    y += 7;

    if (invoice.tax_amount > 0) {
      doc.text(`Tax (${invoice.tax_rate || 0}%):`, 120, y);
      doc.text(`$${(invoice.tax_amount || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 7;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 120, y);
    doc.text(`$${(invoice.total || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    y += 8;

    if (invoice.amount_paid > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 128, 0);
      doc.text('Paid:', 120, y);
      doc.text(`$${(invoice.amount_paid || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
      y += 7;

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Balance Due:', 120, y);
      doc.text(`$${(invoice.balance || 0).toFixed(2)}`, pageWidth - 25, y, { align: 'right' });
    }

    // Notes
    const invoiceNotes = companyInfo?.invoice_notes;
    if (invoiceNotes) {
      y += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Notes:', 20, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const noteLines = doc.splitTextToSize(invoiceNotes, pageWidth - 40);
      doc.text(noteLines, 20, y);
    }

    // Payment terms
    const paymentTerms = companyInfo?.invoice_payment_terms;
    if (paymentTerms && paymentTerms !== 'Due on Receipt') {
      y += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Payment Terms: ${paymentTerms}`, 20, y);
    }

    // Footer
    y = doc.internal.pageSize.getHeight() - 30;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    const footerText = companyInfo?.invoice_footer_text || 'Thank you for your business!';
    doc.text(footerText, pageWidth / 2, y, { align: 'center' });

    return doc;
  };

  // Download PDF
  const downloadInvoicePDF = async (invoice) => {
    try {
      const { data: items } = await supabaseData
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);

      const doc = generateInvoicePDF(invoice, items || []);
      doc.save(`Invoice-${invoice.invoice_number}.pdf`);
      showSuccess('Invoice downloaded!');
    } catch (error) {
      showError('Failed to download invoice: ' + error.message);
    }
  };

  // Print invoice
  const printInvoice = async (invoice) => {
    try {
      const { data: items } = await supabaseData
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);

      const doc = generateInvoicePDF(invoice, items || []);
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      showError('Failed to print invoice: ' + error.message);
    }
  };

  // Email invoice or receipt
  const emailInvoice = async (invoice, isReceipt = false) => {
    if (!invoice.customers?.email) {
      showError('Customer has no email address on file');
      return;
    }

    const docType = isReceipt ? 'receipt' : 'invoice';
    const docTitle = isReceipt ? 'Receipt' : 'Invoice';

    const confirmed = await showConfirm(
      `Send ${docType} ${invoice.invoice_number} to ${invoice.customers.email}?`,
      { title: `Email ${docTitle}`, confirmText: 'Send' }
    );

    if (!confirmed) return;

    try {
      // Generate PDF
      const { data: items } = await supabaseData
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);

      const doc = generateInvoicePDF(invoice, items || []);
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      // Send via edge function
      const { error } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          to: invoice.customers.email,
          customerName: invoice.customerName,
          invoiceNumber: invoice.invoice_number,
          total: invoice.total,
          balance: invoice.balance,
          dueDate: invoice.due_date,
          companyName: companyInfo?.name || 'Company',
          pdfBase64,
          isReceipt
        }
      });

      if (error) throw error;
      showSuccess(`${docTitle} sent to ${invoice.customers.email}`);
    } catch (error) {
      // If edge function doesn't exist, offer mailto fallback
      const subject = encodeURIComponent(
        isReceipt
          ? `Payment Receipt - Invoice ${invoice.invoice_number} from ${companyInfo?.name || 'Company'}`
          : `Invoice ${invoice.invoice_number} from ${companyInfo?.name || 'Company'}`
      );
      const body = encodeURIComponent(
        isReceipt
          ? `Dear ${invoice.customerName},\n\n` +
            `Thank you for your payment!\n\n` +
            `This is your receipt for Invoice ${invoice.invoice_number}.\n\n` +
            `Amount Paid: $${invoice.total?.toFixed(2)}\n` +
            `Paid On: ${formatDisplayDate(invoice.paid_at?.split('T')[0] || new Date().toISOString().split('T')[0])}\n\n` +
            `Thank you for your business!\n\n` +
            `${companyInfo?.name || 'Company'}`
          : `Dear ${invoice.customerName},\n\n` +
            `Please find attached invoice ${invoice.invoice_number}.\n\n` +
            `Amount Due: $${invoice.balance?.toFixed(2)}\n` +
            `Due Date: ${formatDisplayDate(invoice.due_date)}\n\n` +
            `Thank you for your business!\n\n` +
            `${companyInfo?.name || 'Company'}`
      );
      window.open(`mailto:${invoice.customers.email}?subject=${subject}&body=${body}`);
      showAlert(`Email client opened. Please attach the PDF manually or download it first.`, { title: `Email ${docTitle}` });
    }
  };

  const loadCustomers = async () => {
    try {
      const { data, error } = await supabaseData
        .from('customers')
        .select('id, name, first_name, last_name, email, phone')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);

      // Load invoices with customer and job info
      // Use !job_id hint to specify which FK to use (invoices.job_id -> jobs.id)
      const { data, error } = await supabaseData
        .from('invoices')
        .select(`
          *,
          customers (
            id,
            name,
            first_name,
            last_name,
            phone,
            email,
            address,
            city,
            state,
            zip
          ),
          jobs!job_id (
            id,
            job_number,
            job_type
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process invoices to calculate status
      const processedInvoices = (data || []).map(inv => {
        const balance = inv.total - (inv.amount_paid || 0);
        let status = inv.status;

        // Auto-calculate status based on payments
        if (balance <= 0) {
          status = 'paid';
        } else if (inv.amount_paid > 0) {
          status = 'partial';
        } else if (new Date(inv.due_date) < new Date()) {
          status = 'overdue';
        } else {
          status = 'unpaid';
        }

        return {
          ...inv,
          status,
          balance,
          customerName: inv.customers?.name ||
            `${inv.customers?.first_name || ''} ${inv.customers?.last_name || ''}`.trim() ||
            'Unknown'
        };
      });

      setInvoices(processedInvoices);
    } catch (error) {
      showError('Error loading invoices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedJobs = async () => {
    try {
      // Get jobs that are completed but don't have invoices yet
      const { data, error } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            id,
            name,
            first_name,
            last_name,
            phone,
            email
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'completed')
        .is('invoice_id', null)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
    }
  };

  // Helper to format deposit info
  const formatDeposit = (job) => {
    if (!job.deposit_required || !job.deposit_amount) return null;
    return {
      amount: job.deposit_amount,
      paid: job.deposit_paid,
      label: job.deposit_paid ? `Deposit Paid: $${job.deposit_amount.toFixed(2)}` : `Deposit Required: $${job.deposit_amount.toFixed(2)}`
    };
  };

  const createInvoiceFromJob = async (job) => {
    try {
      // Generate invoice number
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const invoiceNumber = `INV-${year}${month}-${random}`;

      // Calculate due date (14 days from now)
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 14);

      const total = job.quoted_value || job.total || 0;
      // Use tax rate based on sales_tax_mode setting
      const taxRate = salesTaxMode === 'disabled' ? 0 : 0.08;
      const subtotal = taxRate > 0 ? total / (1 + taxRate) : total;
      const tax = total - subtotal;

      // Calculate deposit and balance
      const depositAmount = job.deposit_amount || 0;
      const depositPaid = job.deposit_paid || false;
      const amountPaid = depositPaid ? depositAmount : 0;
      const balanceDue = total - amountPaid;

      const { data: newInvoice, error } = await supabaseData
        .from('invoices')
        .insert([{
          company_id: companyId,
          customer_id: job.customer_id,
          job_id: job.id,
          invoice_number: invoiceNumber,
          due_date: dueDate.toISOString().split('T')[0],
          subtotal: subtotal,
          tax_amount: tax,
          tax_rate: 0,
          total: total,
          amount_paid: amountPaid,
          deposit_amount: depositAmount,
          deposit_paid: depositPaid,
          balance_due: balanceDue,
          status: amountPaid >= total ? 'paid' : 'sent',
          notes: `Invoice for Job ${job.job_number}${depositAmount > 0 ? ` (Deposit: $${depositAmount.toFixed(2)}${depositPaid ? ' - PAID' : ' - Required'})` : ''}`
        }])
        .select()
        .single();

      if (error) throw error;

      // Update job with invoice_id
      await supabaseData
        .from('jobs')
        .update({ invoice_id: newInvoice.id })
        .eq('id', job.id);

      showSuccess(`Invoice ${invoiceNumber} created!`);
      setShowCreateModal(false);
      loadInvoices();
    } catch (error) {
      showError('Error creating invoice: ' + error.message);
    }
  };

  const recordPayment = async () => {
    if (!showAddPayment || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showError('Payment amount must be greater than zero');
      return;
    }

    try {
      setSavingPayment(true);

      // Insert payment record (optional - table may not exist)
      try {
        await supabaseData
          .from('payments')
          .insert([{
            company_id: companyId,
            customer_id: showAddPayment.customer_id,
            invoice_id: showAddPayment.id,
            amount: amount,
            payment_method: paymentMethod,
            payment_date: new Date().toISOString().split('T')[0],
            notes: paymentNote || null
          }]);
      } catch (e) {
        // Payments table may not exist - continue anyway
      }

      // Update invoice amount_paid
      const newAmountPaid = (showAddPayment.amount_paid || 0) + amount;
      const newStatus = newAmountPaid >= showAddPayment.total ? 'paid' : 'sent';

      // Create payment method details for this payment
      const newPaymentInfo = {
        type: paymentMethod,
        paid_via: 'manual',
        amount: amount * 100, // Store in cents for consistency with Stripe
        paid_at: new Date().toISOString()
      };

      // Add note if provided (e.g., check number)
      if (paymentNote) {
        newPaymentInfo.note = paymentNote;
      }

      // Append to existing payment method details (supports split payments)
      let updatedPaymentDetails;
      const existing = showAddPayment.payment_method_details;
      if (Array.isArray(existing)) {
        updatedPaymentDetails = [...existing, newPaymentInfo];
      } else if (existing) {
        updatedPaymentDetails = [existing, newPaymentInfo];
      } else {
        updatedPaymentDetails = newPaymentInfo;
      }

      const { error: updateError } = await supabaseData
        .from('invoices')
        .update({
          amount_paid: newAmountPaid,
          status: newStatus,
          payment_method_details: updatedPaymentDetails,
          paid_at: newStatus === 'paid' ? new Date().toISOString() : showAddPayment.paid_at
        })
        .eq('id', showAddPayment.id);

      if (updateError) throw updateError;

      showSuccess('Payment recorded!');
      setShowAddPayment(null);
      setPaymentAmount('');
      setPaymentMethod('cash');
      setPaymentNote('');
      loadInvoices();
    } catch (error) {
      showError('Error recording payment: ' + error.message);
    } finally {
      setSavingPayment(false);
    }
  };

  const createDirectInvoice = async () => {
    if (!newInvoice.customer_id || !newInvoice.amount) {
      showError('Please select a customer and enter an amount');
      return;
    }

    try {
      setSavingInvoice(true);

      // Generate invoice number
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const invoiceNumber = `INV-${year}${month}-${random}`;

      // Calculate amounts
      const subtotal = parseFloat(newInvoice.amount);
      const taxRate = parseFloat(newInvoice.tax_rate) / 100;
      const tax = subtotal * taxRate;

      // Calculate processing fee if enabled
      const processingFee = addProcessingFee ? (subtotal + tax) * (defaultFeePercent / 100) : 0;
      const processingFeePercent = addProcessingFee ? defaultFeePercent : 0;

      const total = subtotal + tax + processingFee;

      // Calculate due date
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + parseInt(newInvoice.due_days));

      // Get customer email for invoice
      const customer = customers.find(c => c.id === newInvoice.customer_id);

      const { data: invoice, error } = await supabaseData
        .from('invoices')
        .insert([{
          company_id: companyId,
          customer_id: newInvoice.customer_id,
          invoice_number: invoiceNumber,
          due_date: dueDate.toISOString().split('T')[0],
          subtotal: subtotal,
          tax_rate: taxRate,
          tax_amount: tax,
          total: total,
          amount_paid: 0,
          balance_due: total,
          status: 'draft',
          notes: newInvoice.notes || newInvoice.description || null
        }])
        .select()
        .single();

      if (error) throw error;

      showSuccess(`Invoice ${invoiceNumber} created for $${total.toFixed(2)}!`);
      setShowDirectCreate(false);
      setAddProcessingFee(false);
      setNewInvoice({
        customer_id: '',
        description: '',
        amount: '',
        tax_rate: salesTaxMode === 'disabled' ? 0 : 8,
        due_days: 14,
        notes: ''
      });
      loadInvoices();
    } catch (error) {
      showError('Error creating invoice: ' + error.message);
    } finally {
      setSavingInvoice(false);
    }
  };

  const getCustomerDisplayName = (customer) => {
    if (!customer) return 'Unknown';
    return customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
  };

  const copyPaymentLink = async (invoice) => {
    const paymentUrl = `${window.location.origin}/pay/${invoice.id}`;
    try {
      await navigator.clipboard.writeText(paymentUrl);
      showSuccess('Payment link copied to clipboard!');
    } catch (err) {
      showAlert(`Payment link: ${paymentUrl}`, { title: 'Payment Link' });
    }
  };

  // Handle Stripe payment - creates checkout session and redirects
  const handleStripePayment = async (invoice) => {
    try {
      const response = await fetch('/api/connect-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId: invoice.id,
          amount: Math.round(invoice.balance * 100), // Convert to cents
          platformFee: Math.round(invoice.balance * 0.01 * 100), // 1% platform fee in cents
          connectedAccountId: companyInfo?.stripe_connect_id,
          customerEmail: invoice.customers?.email,
          description: `Invoice #${invoice.invoice_number}`,
          companyName: companyName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      showError(`Payment error: ${error.message}`);
    }
  };

  const getStatusInfo = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch =
      invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.jobs?.job_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    totalInvoices: invoices.length,
    unpaid: invoices.filter(i => i.status === 'unpaid').length,
    partial: invoices.filter(i => i.status === 'partial').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalOutstanding: invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + (i.balance || 0), 0),
    totalRevenue: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0)
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        color: colors.textMuted
      }}>
        Loading invoices...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: embedded ? 'auto' : '100vh',
      background: embedded ? 'transparent' : `linear-gradient(135deg, ${colors.bgCard} 0%, ${colors.bgInput} 100%)`,
      fontFamily: '"Archivo", sans-serif',
      color: colors.text
    }}>
      {/* Header - hidden when embedded */}
      {!embedded && (
      <div style={{
        background: colors.accentGradient,
        padding: '1rem',
        borderBottom: `4px solid ${colors.accent}`
      }}>
        <div className="invoice-header-wrap" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '0.25rem'
            }}>
              <FileText size={28} color={colors.bgCard} strokeWidth={2.5} />
              <h1 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: colors.bgCard,
                margin: 0
              }}>
                Invoicing
              </h1>
            </div>
            <p style={{
              color: colors.bgInput,
              fontSize: '0.9rem',
              fontWeight: '500',
              margin: 0
            }}>
              Generate invoices and track payments
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowDirectCreate(true)}
              style={{
                background: colors.bgCard,
                color: colors.success,
                border: 'none',
                padding: '0.875rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={20} />
              New Invoice
            </button>
            <button
              onClick={() => {
                loadCompletedJobs();
                setShowCreateModal(true);
              }}
              style={{
                background: 'transparent',
                color: colors.bgCard,
                border: `2px solid ${colors.bgCard}`,
                padding: '0.875rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FileText size={18} />
              From Job
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Main Content */}
      <div style={{ padding: embedded ? '0' : '1rem' }}>
        {/* Mobile-friendly styles injected */}
        <style>{`
          @media (max-width: 768px) {
            .invoice-filters-grid { grid-template-columns: 1fr !important; }
            .invoice-card-grid { grid-template-columns: 1fr !important; }
            .invoice-actions { min-width: 100% !important; margin-top: 1rem; }
            .invoice-totals-flex { gap: 1rem !important; }
            .invoice-header-wrap { flex-direction: column !important; align-items: flex-start !important; }
          }
        `}</style>

        {/* Embedded Action Bar */}
        {embedded && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <button
              onClick={() => setShowDirectCreate(true)}
              style={{
                background: colors.success,
                color: '#fff',
                border: 'none',
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={18} />
              New Invoice
            </button>
            <button
              onClick={() => {
                loadCompletedJobs();
                setShowCreateModal(true);
              }}
              style={{
                background: colors.bgCard,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                padding: '0.75rem 1.25rem',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FileText size={18} />
              From Job
            </button>
          </div>
        )}

        {/* Stats Dashboard */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {[
            { label: 'Total', value: stats.totalInvoices, icon: FileText, color: colors.accent, filter: 'all' },
            { label: 'Unpaid', value: stats.unpaid, icon: AlertCircle, color: colors.error, filter: 'unpaid' },
            { label: 'Partial', value: stats.partial, icon: Clock, color: colors.warning, filter: 'partial' },
            { label: 'Overdue', value: stats.overdue, icon: AlertCircle, color: colors.error, filter: 'overdue' },
            { label: 'Paid', value: stats.paid, icon: CheckCircle, color: colors.success, filter: 'paid' },
            { label: 'Outstanding', value: `$${stats.totalOutstanding.toLocaleString()}`, icon: DollarSign, color: colors.error, filter: null },
            { label: 'Revenue', value: `$${stats.totalRevenue.toLocaleString()}`, icon: DollarSign, color: colors.success, filter: null }
          ].map((stat, idx) => {
            const Icon = stat.icon;
            const isClickable = stat.filter !== null;
            const isActive = isClickable && filterStatus === stat.filter;
            return (
              <div
                key={idx}
                onClick={() => isClickable && setFilterStatus(isActive ? 'all' : stat.filter)}
                style={{
                  background: isActive ? `${stat.color}15` : colors.bgCard,
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: `1px solid ${isActive ? stat.color : colors.border}`,
                  borderLeft: `4px solid ${stat.color}`,
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  transform: isActive ? 'scale(1.02)' : 'none'
                }}
              >
                <Icon size={20} color={stat.color} style={{ marginBottom: '0.5rem' }} />
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: stat.color,
                  marginBottom: '0.25rem'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: isActive ? stat.color : colors.textMuted,
                  textTransform: 'uppercase',
                  fontWeight: isActive ? '700' : '400'
                }}>
                  {stat.label}{isActive ? ' ✓' : ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div style={{
          background: colors.bgCard,
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: `1px solid ${colors.border}`
        }}>
          <div className="invoice-filters-grid" style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '1rem'
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted
              }} />
              <input
                type="text"
                placeholder="Search by customer, invoice, or job number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  fontSize: '0.95rem'
                }}
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '0.75rem 2.25rem 0.75rem 1rem',
                background: `${colors.bgCard} url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") right 0.75rem center / 14px no-repeat`,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.95rem',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none'
              }}
            >
              <option value="all">All Statuses</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: colors.textMuted
          }}>
            <FileText size={64} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: colors.text }}>
              {invoices.length === 0 ? 'No invoices yet' : 'No invoices found'}
            </h3>
            <p>{invoices.length === 0 ? 'Create your first invoice from a completed job' : 'Try adjusting your filters'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {filteredInvoices.map(invoice => {
              const statusInfo = getStatusInfo(invoice.status);
              const StatusIcon = statusInfo.icon;

              return (
                <div
                  key={invoice.id}
                  style={{
                    background: colors.bgCard,
                    borderRadius: '12px',
                    border: `1px solid ${colors.border}`,
                    overflow: 'hidden'
                  }}
                >
                  <div className="invoice-card-grid" style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '1rem',
                    alignItems: 'start'
                  }}>
                    {/* Left - Invoice Info */}
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginBottom: '1rem',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: `${statusInfo.color}20`,
                          border: `2px solid ${statusInfo.color}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <StatusIcon size={20} color={statusInfo.color} />
                        </div>

                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.25rem',
                            flexWrap: 'wrap'
                          }}>
                            <span style={{
                              fontSize: '0.85rem',
                              fontWeight: '700',
                              color: colors.textMuted
                            }}>
                              {invoice.invoice_number}
                            </span>
                            {invoice.jobs?.job_number && (
                              <>
                                <span style={{ color: colors.textMuted }}>•</span>
                                <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                                  Job: {invoice.jobs.job_number}
                                </span>
                              </>
                            )}
                          </div>
                          <h3 style={{
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            color: colors.text,
                            margin: 0
                          }}>
                            {invoice.customerName}
                          </h3>
                        </div>

                        {/* Hide status badge for $0 invoices — "PAID" on $0 is misleading */}
                        {(invoice.total || 0) > 0 && (
                          <div style={{
                            padding: '0.4rem 1rem',
                            background: statusInfo.color,
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '700',
                            color: colors.bgCard,
                            textTransform: 'uppercase'
                          }}>
                            {statusInfo.label}
                          </div>
                        )}
                      </div>

                      {/* Customer Info */}
                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginBottom: '1rem'
                      }}>
                        {invoice.customers?.phone && (
                          <div style={{
                            padding: '0.5rem 0.75rem',
                            background: colors.bgCard,
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.85rem',
                            color: colors.textMuted
                          }}>
                            <Phone size={14} color={colors.textMuted} />
                            {invoice.customers.phone}
                          </div>
                        )}
                        {invoice.customers?.email && (
                          <div style={{
                            padding: '0.5rem 0.75rem',
                            background: colors.bgCard,
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.85rem',
                            color: colors.textMuted
                          }}>
                            <Mail size={14} color={colors.textMuted} />
                            {invoice.customers.email}
                          </div>
                        )}
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          background: colors.bgCard,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.85rem',
                          color: colors.textMuted
                        }}>
                          <Calendar size={14} color={colors.textMuted} />
                          Due: {formatDisplayDate(invoice.due_date)}
                        </div>
                      </div>

                      {/* Totals */}
                      <div className="invoice-totals-flex" style={{
                        display: 'flex',
                        gap: '1.5rem',
                        padding: '0.75rem',
                        background: colors.bgCard,
                        borderRadius: '8px',
                        flexWrap: 'wrap'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Total</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.text }}>
                            ${invoice.total?.toFixed(2)}
                          </div>
                        </div>
                        {invoice.deposit_amount > 0 && (
                          <div>
                            <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Deposit</div>
                            <div style={{
                              fontSize: '1.25rem',
                              fontWeight: '700',
                              color: invoice.deposit_paid ? colors.success : colors.warning
                            }}>
                              ${invoice.deposit_amount?.toFixed(2)}
                              {invoice.deposit_paid && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>PAID</span>}
                            </div>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Paid</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.success }}>
                            ${(invoice.amount_paid || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Balance</div>
                          <div style={{
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            color: invoice.balance > 0 ? colors.warning : colors.success
                          }}>
                            ${invoice.balance?.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right - Actions */}
                    <div className="invoice-actions" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      minWidth: '160px'
                    }}>
                      {invoice.status !== 'paid' && (
                        <>
                          {/* Collect Online Payment */}
                          {(companyStripeId || companyInfo?.square_merchant_id) ? (
                            <PayInvoiceButton
                              invoice={invoice}
                              companyId={companyId}
                              companyStripeAccountId={companyStripeId}
                              companySquareMerchantId={companyInfo?.square_merchant_id}
                              companySquareLocationId={companyInfo?.square_location_id}
                              companyName={companyName}
                            />
                          ) : (
                            <button
                              onClick={() => onNavigate && onNavigate('settings', { section: 'payment' })}
                              style={{
                                padding: '0.875rem',
                                background: 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '0.9rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              💳 Connect Stripe
                            </button>
                          )}

                          {/* Record Manual Payment */}
                          <button
                            onClick={() => setShowAddPayment(invoice)}
                            style={{
                              padding: '0.75rem',
                              background: colors.success,
                              border: 'none',
                              borderRadius: '8px',
                              color: colors.bgCard,
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <DollarSign size={16} />
                            Record Payment
                          </button>
                        </>
                      )}

                      {/* View Invoice Button */}
                      <button
                        onClick={() => viewInvoice(invoice)}
                        style={{
                          padding: '0.75rem',
                          background: colors.info,
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Eye size={14} />
                        View Invoice
                      </button>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '0.5rem'
                      }}>
                        <button
                          onClick={() => printInvoice(invoice)}
                          style={{
                            padding: '0.75rem',
                            background: colors.border,
                            border: 'none',
                            borderRadius: '8px',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <Printer size={14} />
                          Print
                        </button>

                        <button
                          onClick={() => emailInvoice(invoice)}
                          style={{
                            padding: '0.75rem',
                            background: colors.border,
                            border: 'none',
                            borderRadius: '8px',
                            color: colors.text,
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem'
                          }}
                        >
                          <Send size={14} />
                          Email
                        </button>
                      </div>

                      <button
                        onClick={() => downloadInvoicePDF(invoice)}
                        style={{
                          padding: '0.75rem',
                          background: colors.border,
                          border: 'none',
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Download size={14} />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1.5rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '85vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: colors.text }}>Create Invoice</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {jobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                  <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                  <p>No completed jobs without invoices.</p>
                  <p style={{ fontSize: '0.85rem' }}>Complete a job first, then create an invoice from it.</p>
                </div>
              ) : (
                <>
                  <p style={{ color: colors.textMuted, marginBottom: '1.5rem' }}>
                    Select a completed job to create an invoice:
                  </p>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {jobs.map(job => {
                      const deposit = formatDeposit(job);
                      return (
                        <div
                          key={job.id}
                          onClick={() => createInvoiceFromJob(job)}
                          style={{
                            background: colors.bgCard,
                            padding: '1.25rem',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'border-color 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.borderColor = colors.accent}
                          onMouseOut={(e) => e.currentTarget.style.borderColor = colors.border}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start'
                          }}>
                            <div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: colors.textMuted,
                                marginBottom: '0.25rem'
                              }}>
                                {job.job_number}
                              </div>
                              <div style={{
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                color: colors.text
                              }}>
                                {job.customers?.name ||
                                  `${job.customers?.first_name || ''} ${job.customers?.last_name || ''}`.trim()}
                              </div>
                              {job.job_type && (
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: colors.textMuted,
                                  marginTop: '0.25rem'
                                }}>
                                  {job.job_type}
                                </div>
                              )}
                              {deposit && (
                                <div style={{
                                  marginTop: '0.5rem',
                                  padding: '0.35rem 0.75rem',
                                  background: deposit.paid ? `${colors.success}20` : `${colors.warning}20`,
                                  borderRadius: '4px',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  color: deposit.paid ? colors.success : colors.warning,
                                  display: 'inline-block'
                                }}>
                                  {deposit.label}
                                </div>
                              )}
                            </div>
                            <div style={{
                              fontSize: '1.25rem',
                              fontWeight: '700',
                              color: colors.success
                            }}>
                              ${(job.quoted_value || job.total || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showAddPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '450px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: colors.text }}>Record Payment</h2>
              <button
                onClick={() => {
                  setShowAddPayment(null);
                  setPaymentAmount('');
                  setPaymentMethod('cash');
                  setPaymentNote('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              <div style={{
                background: colors.bgCard,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {showAddPayment.invoice_number}
                </div>
                <div style={{ color: colors.text, fontWeight: '700', marginTop: '0.25rem' }}>
                  Balance Due: ${showAddPayment.balance?.toFixed(2)}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: '600'
                }}>
                  Amount *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Block negative values and non-numeric input
                    if (v === '' || (/^\d*\.?\d{0,2}$/.test(v) && parseFloat(v) >= 0)) {
                      setPaymentAmount(v);
                    }
                  }}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1.25rem',
                    fontWeight: '700'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: '600'
                }}>
                  Payment Method
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.5rem'
                }}>
                  {paymentMethods.map(method => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setPaymentMethod(method.value)}
                        style={{
                          padding: '0.75rem',
                          background: paymentMethod === method.value ? colors.accent : colors.bgInput,
                          border: paymentMethod === method.value ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: paymentMethod === method.value ? '#fff' : colors.text,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          fontSize: '0.9rem',
                          fontWeight: paymentMethod === method.value ? '700' : '500'
                        }}
                      >
                        <Icon size={16} />
                        {method.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  Memo (optional)
                </label>
                <textarea
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  rows={2}
                  placeholder={
                    paymentMethod === 'check' ? 'e.g., Check #1234' :
                    paymentMethod === 'bank_transfer' ? 'e.g., ACH txn 9A1B2C — Wells Fargo' :
                    paymentMethod === 'credit_card' ? 'e.g., Last 4: 4242 — reference: AUTH123' :
                    paymentMethod === 'cash' ? 'e.g., Paid in full at jobsite' :
                    'Reference #, check #, or any note for your records'
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowAddPayment(null);
                    setPaymentAmount('');
                    setPaymentMethod('cash');
                    setPaymentNote('');
                  }}
                  disabled={savingPayment}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={recordPayment}
                  disabled={!paymentAmount || savingPayment}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: paymentAmount && !savingPayment ? colors.success : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: paymentAmount && !savingPayment ? colors.bgCard : colors.textMuted,
                    fontWeight: '700',
                    cursor: paymentAmount && !savingPayment ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <DollarSign size={18} />
                  {savingPayment ? 'Saving...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Direct Create Invoice Modal */}
      {showDirectCreate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1.5rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '85vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: colors.text }}>Create Invoice</h2>
              <button
                onClick={() => setShowDirectCreate(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* Customer Selection */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: '600'
                }}>
                  Customer *
                </label>
                <select
                  value={newInvoice.customer_id}
                  onChange={(e) => setNewInvoice({ ...newInvoice, customer_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select a customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {getCustomerDisplayName(customer)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: '600'
                }}>
                  Description
                </label>
                <input
                  type="text"
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                  placeholder="e.g., Door repair service"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Amount and Tax Rate */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.text,
                    fontWeight: '600'
                  }}>
                    Amount (before tax) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newInvoice.amount}
                    onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}
                  />
                </div>
                {salesTaxMode !== 'disabled' && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: colors.text,
                      fontWeight: '600'
                    }}>
                      Tax Rate %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={newInvoice.tax_rate}
                      onChange={(e) => setNewInvoice({ ...newInvoice, tax_rate: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Due Days */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: '600'
                }}>
                  Due in (days)
                </label>
                <select
                  value={newInvoice.due_days}
                  onChange={(e) => setNewInvoice({ ...newInvoice, due_days: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="0">Due Today</option>
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                </select>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  Notes (optional)
                </label>
                <textarea
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                  rows={2}
                  placeholder="Additional notes..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '0.95rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Processing Fee Option */}
              {processingFeeEnabled && companyStripeId && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: colors.bgCard,
                  borderRadius: '8px',
                  border: addProcessingFee ? '1px solid #f39c1250' : `1px solid ${colors.border}`
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={addProcessingFee}
                      onChange={(e) => setAddProcessingFee(e.target.checked)}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#f39c12'
                      }}
                    />
                    <div>
                      <div style={{ color: colors.text, fontWeight: '600' }}>
                        Add Card Processing Fee ({defaultFeePercent}%)
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        Pass credit card processing costs to customer
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Total Preview */}
              {newInvoice.amount && (
                <div style={{
                  background: colors.bgCard,
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: colors.textMuted }}>Subtotal</span>
                    <span style={{ color: colors.text }}>${parseFloat(newInvoice.amount || 0).toFixed(2)}</span>
                  </div>
                  {salesTaxMode !== 'disabled' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ color: colors.textMuted }}>Tax ({newInvoice.tax_rate}%)</span>
                      <span style={{ color: colors.text }}>
                        ${(parseFloat(newInvoice.amount || 0) * (parseFloat(newInvoice.tax_rate) / 100)).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {addProcessingFee && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ color: '#f39c12' }}>Card Processing Fee ({defaultFeePercent}%)</span>
                      <span style={{ color: '#f39c12' }}>
                        ${((parseFloat(newInvoice.amount || 0) * (1 + (salesTaxMode === 'disabled' ? 0 : parseFloat(newInvoice.tax_rate)) / 100)) * (defaultFeePercent / 100)).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.text, fontWeight: '700' }}>Total</span>
                    <span style={{ color: colors.success, fontWeight: '700', fontSize: '1.25rem' }}>
                      ${(() => {
                        const subtotal = parseFloat(newInvoice.amount || 0);
                        const taxRate = salesTaxMode === 'disabled' ? 0 : parseFloat(newInvoice.tax_rate);
                        const afterTax = subtotal * (1 + taxRate / 100);
                        const processingFee = addProcessingFee ? afterTax * (defaultFeePercent / 100) : 0;
                        return (afterTax + processingFee).toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowDirectCreate(false);
                    setAddProcessingFee(false);
                    setNewInvoice({
                      customer_id: '',
                      description: '',
                      amount: '',
                      tax_rate: salesTaxMode === 'disabled' ? 0 : 8,
                      due_days: 14,
                      notes: ''
                    });
                  }}
                  disabled={savingInvoice}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={createDirectInvoice}
                  disabled={!newInvoice.customer_id || !newInvoice.amount || savingInvoice}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: newInvoice.customer_id && newInvoice.amount && !savingInvoice ? colors.success : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: newInvoice.customer_id && newInvoice.amount && !savingInvoice ? colors.bgCard : colors.textMuted,
                    fontWeight: '700',
                    cursor: newInvoice.customer_id && newInvoice.amount && !savingInvoice ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FileText size={18} />
                  {savingInvoice ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewingInvoice && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setViewingInvoice(null); setInvoiceItems([]); } }}
          style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            position: 'relative',
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Invoice Header */}
            <div style={{
              padding: '2rem',
              borderBottom: '2px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h2 style={{
                  margin: 0,
                  color: '#333',
                  fontSize: '1.5rem',
                  fontWeight: '700'
                }}>
                  {companyInfo?.name || 'Company'}
                </h2>
                {companyInfo?.phone && (
                  <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    {companyInfo.phone}
                  </div>
                )}
                {companyInfo?.email && (
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>
                    {companyInfo.email}
                  </div>
                )}
                {companyInfo?.address && (
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>
                    {companyInfo.address}
                    {companyInfo.city && `, ${companyInfo.city}`}
                    {companyInfo.state && `, ${companyInfo.state}`}
                    {companyInfo.zip && ` ${companyInfo.zip}`}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <h1 style={{
                  margin: 0,
                  color: colors.accent,
                  fontSize: '2rem',
                  fontWeight: '700'
                }}>
                  INVOICE
                </h1>
                <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  <div><strong>Invoice #:</strong> {viewingInvoice.invoice_number}</div>
                  <div><strong>Date:</strong> {formatDisplayDate(viewingInvoice.created_at?.split('T')[0])}</div>
                  <div><strong>Due:</strong> {formatDisplayDate(viewingInvoice.due_date)}</div>
                </div>
                <button
                  onClick={() => {
                    setViewingInvoice(null);
                    setInvoiceItems([]);
                  }}
                  style={{
                    position: 'absolute',
                    top: '0.75rem',
                    right: '0.75rem',
                    background: '#e0e0e0',
                    border: '1px solid #ccc',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}
                >
                  <X size={22} color="#333" />
                </button>
              </div>
            </div>

            {/* Bill To */}
            <div style={{ padding: '1.5rem 2rem' }}>
              <h3 style={{
                margin: '0 0 0.5rem 0',
                color: '#333',
                fontSize: '0.9rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Bill To
              </h3>
              <div style={{ color: '#333', fontSize: '1rem', fontWeight: '600' }}>
                {viewingInvoice.customerName}
              </div>
              {viewingInvoice.customers?.phone && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  {viewingInvoice.customers.phone}
                </div>
              )}
              {viewingInvoice.customers?.email && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  {viewingInvoice.customers.email}
                </div>
              )}
              {viewingInvoice.customers?.address && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  {viewingInvoice.customers.address}
                  {viewingInvoice.customers.city && `, ${viewingInvoice.customers.city}`}
                  {viewingInvoice.customers.state && `, ${viewingInvoice.customers.state}`}
                  {viewingInvoice.customers.zip && ` ${viewingInvoice.customers.zip}`}
                </div>
              )}
            </div>

            {/* Line Items Table */}
            <div style={{ padding: '0 2rem' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem'
              }}>
                <thead>
                  <tr style={{ background: '#333' }}>
                    <th style={{
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      color: '#fff',
                      fontWeight: '600'
                    }}>
                      Description
                    </th>
                    <th style={{
                      padding: '0.75rem 1rem',
                      textAlign: 'center',
                      color: '#fff',
                      fontWeight: '600',
                      width: '80px'
                    }}>
                      Qty
                    </th>
                    <th style={{
                      padding: '0.75rem 1rem',
                      textAlign: 'right',
                      color: '#fff',
                      fontWeight: '600',
                      width: '100px'
                    }}>
                      Price
                    </th>
                    <th style={{
                      padding: '0.75rem 1rem',
                      textAlign: 'right',
                      color: '#fff',
                      fontWeight: '600',
                      width: '100px'
                    }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.length > 0 ? (
                    invoiceItems.map((item, idx) => (
                      <tr key={item.id || idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '0.75rem 1rem', color: '#333' }}>
                          {item.description || item.name || 'Item'}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#333' }}>
                          {item.quantity || 1}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#333' }}>
                          ${(item.unit_price || item.price || 0).toFixed(2)}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#333', fontWeight: '600' }}>
                          ${((item.quantity || 1) * (item.unit_price || item.price || 0)).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '0.75rem 1rem', color: '#333' }}>
                        Services rendered
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'center', color: '#333' }}>
                        1
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#333' }}>
                        ${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#333', fontWeight: '600' }}>
                        ${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{
              padding: '1.5rem 2rem',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <div style={{ width: '250px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <span style={{ color: '#666' }}>Subtotal</span>
                  <span style={{ color: '#333' }}>
                    ${(viewingInvoice.subtotal || viewingInvoice.total || 0).toFixed(2)}
                  </span>
                </div>
                {(viewingInvoice.tax_amount || viewingInvoice.tax) > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <span style={{ color: '#666' }}>
                      Tax {viewingInvoice.tax_rate ? `(${(viewingInvoice.tax_rate * 100).toFixed(0)}%)` : ''}
                    </span>
                    <span style={{ color: '#333' }}>
                      ${(viewingInvoice.tax_amount || viewingInvoice.tax || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0',
                  borderBottom: '2px solid #333'
                }}>
                  <span style={{ color: '#333', fontWeight: '700', fontSize: '1.1rem' }}>Total</span>
                  <span style={{ color: '#333', fontWeight: '700', fontSize: '1.1rem' }}>
                    ${(viewingInvoice.total || 0).toFixed(2)}
                  </span>
                </div>
                {viewingInvoice.amount_paid > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0'
                  }}>
                    <span style={{ color: '#22c55e' }}>Paid</span>
                    <span style={{ color: '#22c55e' }}>
                      -${(viewingInvoice.amount_paid || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem 0',
                  background: viewingInvoice.balance > 0 ? '#fef3c7' : '#dcfce7',
                  margin: '0.5rem -0.5rem 0',
                  padding: '0.75rem',
                  borderRadius: '6px'
                }}>
                  <span style={{
                    color: viewingInvoice.balance > 0 ? '#92400e' : '#166534',
                    fontWeight: '700',
                    fontSize: '1.1rem'
                  }}>
                    Balance Due
                  </span>
                  <span style={{
                    color: viewingInvoice.balance > 0 ? '#92400e' : '#166534',
                    fontWeight: '700',
                    fontSize: '1.1rem'
                  }}>
                    ${(viewingInvoice.balance || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Method Details - shown for paid/partial invoices */}
            {viewingInvoice.payment_method_details && (
              <div style={{
                padding: '1rem 2rem',
                borderTop: '1px solid #e0e0e0',
                background: '#f8fafc'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.85rem' }}>Payment Method</h4>
                {(() => {
                  const payments = Array.isArray(viewingInvoice.payment_method_details)
                    ? viewingInvoice.payment_method_details
                    : [viewingInvoice.payment_method_details];

                  // Helper to get display info for payment type
                  const getPaymentDisplay = (pm) => {
                    // Manual payment types
                    if (pm.type === 'cash') return { label: 'Cash', abbr: 'CASH', color: '#22c55e' };
                    if (pm.type === 'check') return { label: `Check${pm.note ? ` #${pm.note}` : ''}`, abbr: 'CHK', color: '#3b82f6' };
                    if (pm.type === 'credit_card') return { label: 'Credit Card', abbr: 'CARD', color: '#6366f1' };
                    if (pm.type === 'bank_transfer') return { label: 'Bank Transfer', abbr: 'BANK', color: '#0ea5e9' };
                    // Stripe card payments
                    if (pm.brand === 'visa') return { label: `Visa •••• ${pm.last4 || ''}`, abbr: 'VISA', color: '#1a1f71' };
                    if (pm.brand === 'mastercard') return { label: `Mastercard •••• ${pm.last4 || ''}`, abbr: 'MC', color: '#eb001b' };
                    if (pm.brand === 'amex') return { label: `Amex •••• ${pm.last4 || ''}`, abbr: 'AMEX', color: '#006fcf' };
                    if (pm.brand === 'discover') return { label: `Discover •••• ${pm.last4 || ''}`, abbr: 'DISC', color: '#ff6000' };
                    // BNPL
                    if (pm.brand === 'afterpay' || pm.type === 'afterpay_clearpay') return { label: 'Afterpay', abbr: 'AFPY', color: '#b2fce4', textColor: '#000' };
                    if (pm.brand === 'klarna' || pm.type === 'klarna') return { label: 'Klarna', abbr: 'KLRN', color: '#ffb3c7', textColor: '#000' };
                    if (pm.brand === 'affirm' || pm.type === 'affirm') return { label: 'Affirm', abbr: 'AFRM', color: '#4a4af4' };
                    // Terminal
                    if (pm.type === 'card_present') return { label: `Card •••• ${pm.last4 || ''}`, abbr: pm.brand?.slice(0, 4)?.toUpperCase() || 'CARD', color: '#6b7280' };
                    // Fallback
                    return { label: pm.brand || pm.type || 'Payment', abbr: (pm.brand || pm.type || 'PAY').slice(0, 4).toUpperCase(), color: '#6b7280' };
                  };

                  return payments.map((pm, idx) => {
                    const display = getPaymentDisplay(pm);
                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem 0.75rem',
                        background: '#fff',
                        borderRadius: '6px',
                        marginBottom: idx < payments.length - 1 ? '0.5rem' : 0,
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          background: display.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: display.textColor || '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 'bold'
                        }}>
                          {display.abbr}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#1f2937', fontWeight: '500', fontSize: '0.9rem' }}>
                            {display.label}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                            {pm.paid_via === 'terminal' ? 'In-person' : pm.paid_via === 'online' ? 'Online' : pm.paid_via === 'manual' ? 'Recorded manually' : ''}
                            {pm.amount && payments.length > 1 && ` • $${(pm.amount / 100).toFixed(2)}`}
                          </div>
                        </div>
                        <CheckCircle size={16} color="#22c55e" />
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Notes */}
            {viewingInvoice.notes && (
              <div style={{
                padding: '1rem 2rem',
                borderTop: '1px solid #e0e0e0'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.85rem' }}>Notes</h4>
                <p style={{ margin: 0, color: '#333', fontSize: '0.9rem' }}>
                  {viewingInvoice.notes}
                </p>
              </div>
            )}

            {/* Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
              color: '#999',
              fontSize: '0.85rem'
            }}>
              Thank you for your business!
            </div>

            {/* Action Buttons */}
            <div style={{
              padding: '1rem 2rem 2rem',
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => printInvoice(viewingInvoice)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Printer size={16} />
                Print
              </button>
              <button
                onClick={() => downloadInvoicePDF(viewingInvoice)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Download size={16} />
                Download PDF
              </button>
              <button
                onClick={() => emailInvoice(viewingInvoice)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Send size={16} />
                Email Invoice
              </button>

              {/* Payment/Receipt Buttons - conditional based on status */}
              {viewingInvoice.status === 'paid' ? (
                <button
                  onClick={() => {
                    // Send receipt functionality - reuse email with receipt flag
                    emailInvoice(viewingInvoice, true);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: colors.success,
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Receipt size={16} />
                  Send Receipt
                </button>
              ) : (
                <>
                  {companyInfo?.stripe_connect_id && companyInfo?.stripe_charges_enabled && (
                    <button
                      onClick={() => {
                        // Trigger Stripe checkout for this invoice
                        handleStripePayment(viewingInvoice);
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#6366f1',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <CreditCard size={16} />
                      Collect Payment - Stripe
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setViewingInvoice(null);
                      setShowAddPayment(viewingInvoice);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: colors.success,
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <DollarSign size={16} />
                    Manual Payment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
